import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, Download, Package, BarChart2, User, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react';
import styles from './styles.module.css';

const sections = [
  {
    id: 'perfil',
    icon: User,
    title: 'Criar perfil da empresa ou prestador',
    description: 'Configure os dados que aparecem em todas as suas notas.',
    steps: [
      'Acesse o menu lateral e clique em Perfil.',
      'Preencha o nome da empresa ou do prestador de serviço.',
      'Faça upload da sua logo ou foto clicando na área de imagem.',
      'Adicione CNPJ/CPF, endereço e contato.',
      'Clique em Salvar — as informações aparecem automaticamente em todas as notas.',
    ],
  },
  {
    id: 'nota',
    icon: FileText,
    title: 'Criar uma nota de serviço',
    description: 'Gere notas profissionais em segundos.',
    steps: [
      'No painel principal, clique em Nova Nota.',
      'Selecione o cliente ou cadastre um novo na hora.',
      'Adicione os serviços ou produtos — busque pelo nome se já estiverem cadastrados.',
      'Confira subtotal, descontos e total calculados automaticamente.',
      'Clique em Salvar. A nota fica no histórico, pronta para exportar.',
    ],
  },
  {
    id: 'pdf',
    icon: Download,
    title: 'Exportar nota em PDF',
    description: 'Toda nota pode ser exportada em PDF com layout profissional.',
    steps: [
      'Abra a nota que deseja exportar.',
      'Clique em Exportar PDF no canto inferior direito.',
      'O arquivo é gerado e baixado automaticamente para o seu dispositivo.',
      'Compartilhe o PDF com o cliente pelo canal de sua preferência.',
    ],
    note: 'O sistema gera o PDF — o envio ao cliente é feito por você fora do sistema.',
  },
  {
    id: 'produtos',
    icon: Package,
    title: 'Cadastrar produtos e serviços',
    description: 'Mantenha um catálogo para agilizar a criação de notas.',
    steps: [
      'Acesse Produtos no menu lateral.',
      'Clique em Novo Produto.',
      'Preencha nome, descrição, unidade de medida e preço padrão.',
      'Salve. O produto fica disponível ao criar notas.',
      'Edite ou inative produtos a qualquer momento.',
    ],
  },
  {
    id: 'estoque',
    icon: BarChart2,
    title: 'Gerenciar estoque',
    description: 'Acompanhe a quantidade disponível de cada produto.',
    steps: [
      'Acesse Estoque no menu lateral.',
      'Visualize a quantidade disponível de cada produto.',
      'Clique em um produto para registrar entrada ou saída manual.',
      'Ao emitir uma nota, o estoque é descontado automaticamente.',
      'Use filtros para localizar itens com estoque baixo.',
    ],
  },
];

const faq = [
  { q: 'Posso usar sem cadastrar produtos?', a: 'Sim. Você pode digitar os itens diretamente na nota, sem precisar ter produtos cadastrados no catálogo.' },
  { q: 'O sistema envia a nota por e-mail?', a: 'No momento o sistema não envia e-mails. Você exporta o PDF e envia pelo canal de sua preferência.' },
  { q: 'O estoque é controlado automaticamente?', a: 'Sim. Ao emitir uma nota com um produto do catálogo, a quantidade em estoque é descontada automaticamente.' },
  { q: 'Posso ter mais de um perfil de empresa?', a: 'Cada conta possui um perfil ativo. Para múltiplas empresas, é necessário criar contas separadas.' },
];

export default function Tutorial() {
  const [activeSection, setActiveSection] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const toggle = (id) => setActiveSection((prev) => (prev === id ? null : id));
  const toggleFaq = (i) => setOpenFaq((prev) => (prev === i ? null : i));

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Como Usar — Tutorial do Orçamento Digital</title>
        <meta
          name="description"
          content="Aprenda passo a passo como usar o Orçamento Digital: criar perfil, gerar orçamentos, controlar estoque e emitir notas de serviço em PDF."
        />
        <link rel="canonical" href="https://www.orcamento-digital.com/tutorial" />
      </Helmet>
      <div className={styles.glow} />

      <header className={styles.hero}>
        <span className={styles.badge}>📖 Como usar o Orçamento Digital</span>
        <h1 className={styles.title}>
          Tudo que você precisa<br />
          <span className={styles.accent}>em um só lugar</span>
        </h1>
        <p className={styles.subtitle}>
          Guia completo das funcionalidades. Clique em qualquer seção para ver o passo a passo.
        </p>
      </header>

      <main className={styles.main}>
        <div className={styles.sections}>
          {sections.map(({ id, icon: Icon, title, description, steps, note }) => {
            const isOpen = activeSection === id;
            return (
              <div key={id} className={`${styles.card} ${isOpen ? styles.cardOpen : ''}`}>
                <button className={styles.cardHeader} onClick={() => toggle(id)}>
                  <div className={styles.cardLeft}>
                    <span className={styles.iconWrap}><Icon size={20} /></span>
                    <div className={styles.cardMeta}>
                      <span className={styles.cardTitle}>{title}</span>
                      <span className={styles.cardDesc}>{description}</span>
                    </div>
                  </div>
                  <ChevronDown size={18} className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
                </button>
                {isOpen && (
                  <div className={styles.cardBody}>
                    <ol className={styles.stepList}>
                      {steps.map((step, i) => (
                        <li key={i} className={styles.stepItem}>
                          <span className={styles.stepNum}>{i + 1}</span>
                          <span className={styles.stepText}>{step}</span>
                        </li>
                      ))}
                    </ol>
                    {note && (
                      <div className={styles.noteBox}>
                        <CheckCircle2 size={15} className={styles.noteIcon} />
                        {note}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <section className={styles.faqSection}>
          <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
          <div className={styles.faqList}>
            {faq.map((item, i) => (
              <div key={i} className={`${styles.faqItem} ${openFaq === i ? styles.faqOpen : ''}`}>
                <button className={styles.faqQ} onClick={() => toggleFaq(i)}>
                  {item.q}
                  <ChevronDown size={16} className={`${styles.chevron} ${openFaq === i ? styles.chevronOpen : ''}`} />
                </button>
                {openFaq === i && <p className={styles.faqA}>{item.a}</p>}
              </div>
            ))}
          </div>
        </section>

        <div className={styles.cta}>
          <p className={styles.ctaText}>Ainda com dúvidas? Comece agora e explore na prática.</p>
          <Link to="/register" className={styles.btnPrimary}>
            Criar conta grátis <ChevronRight size={16} />
          </Link>
        </div>
      </main>
    </div>
  );
}